import { useApolloClient } from "@apollo/client";
import {
  Check,
  ChevronDown,
  ChevronUp,
  EllipsisVertical,
  Split,
  Trash2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";

import { useTableInstanceContext } from "@/components/shared/data-table";
import {
  TableActionBarButton,
  TableActionBarRoot,
  TableActionBarRowsSelected,
} from "@/components/shared/data-table/action-bar";
import { SupplierDropdown } from "@/components/shared/form/supplier-dropdown-input";
import { AccountsDropdown } from "@/components/shared/select-inputs/accounts-dropdown";
import { BuDropdown } from "@/components/shared/select-inputs/bu-dropdown";
import { CostCenterDropdown } from "@/components/shared/select-inputs/cost-center-dropdown";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type {
  InvoiceType,
  InvoiceTypeString,
} from "@/lib/constants/invoice-type";
import { getBookingKey } from "@/lib/miscellaneous/group-by-booking";
import { useTenant } from "@/providers/session";
import { useGetAllAccounts } from "@/queries/accounts/get-all";
import { useGetAllCostCenters } from "@/queries/cost-centers/fetch";
import {
  useUpdateLineItemReviewed,
  useUpdateManyLineItems,
} from "@/queries/line-items/update";
import { useGetAllSuppliers } from "@/queries/suppliers/get-all";
import { useGetAllTaxCodes } from "@/queries/tax-codes/get-all";

import { useDeleteInvoice } from "@/queries/invoices/delete";
import {
  useUpdateInvoices,
  useUpdateInvoicesType,
} from "@/queries/invoices/update";
import { TypeSelect } from "../invoice-overview/invoice-table-action-bar";
import { SpecialFilterValue } from "./types";

type Props = {
  onNextSupplier: () => void;
  onPreviousSupplier: () => void;
  onSupplierDone: () => void;
  onSetSupplier: (supplierId: string) => void;
  disableSupplierNavigation?: boolean;
  invoiceType?: InvoiceTypeString;
};

export const ActionBar = ({
  onSetSupplier,
  onNextSupplier,
  onPreviousSupplier,
  onSupplierDone,
  disableSupplierNavigation = false,
  invoiceType,
}: Props) => {
  const { table } = useTableInstanceContext();
  const { t } = useTranslation();
  const apolloClient = useApolloClient();
  const { accounts } = useGetAllAccounts();
  const { taxCodes } = useGetAllTaxCodes();
  const { costCenters } = useGetAllCostCenters();
  const { data: suppliers } = useGetAllSuppliers({
    type: invoiceType === "receivable" ? "debtor" : "creditor",
  });
  const [updateInvoice] = useUpdateInvoices();
  const [updateInvoiceType] = useUpdateInvoicesType();
  const deleteInvoice = useDeleteInvoice();
  const { hasCostCenters } = useTenant();

  const updateLineItems = useUpdateManyLineItems();
  const approveLineItems = useUpdateLineItemReviewed();

  if (table.getSelectedRowModel().flatRows.length === 0) {
    return (
      <TableActionBarRoot>
        <TableActionBarButton
          variant="ghost"
          className="gap-2 hover:bg-gray-200"
          onClick={onPreviousSupplier}
          disabled={disableSupplierNavigation}
        >
          <ChevronUp size={20} />
          {invoiceType === "receivable"
            ? t("fastlane.action_bar.previous_debtor")
            : t("fastlane.action_bar.previous_creditor")}
        </TableActionBarButton>
        <TableActionBarButton
          variant="ghost"
          className="gap-2 hover:bg-gray-200"
          onClick={onNextSupplier}
          disabled={disableSupplierNavigation}
        >
          <ChevronDown size={20} />
          {invoiceType === "receivable"
            ? t("fastlane.action_bar.next_debtor")
            : t("fastlane.action_bar.next_creditor")}
        </TableActionBarButton>
        <TableActionBarButton
          onClick={onSupplierDone}
          disabled={disableSupplierNavigation}
          className="gap-2"
        >
          <Check size={14} />
          {invoiceType === "receivable"
            ? t("fastlane.action_bar.debtor_done_and_next")
            : t("fastlane.action_bar.creditor_done_and_next")}
        </TableActionBarButton>
      </TableActionBarRoot>
    );
  }

  const lineItemIds = table
    .getSelectedRowModel()
    .flatRows.filter((row) => row.depth === 2)
    .map((row) => row.original.id);

  const setLoadingRows = (value: string | null) => {
    const selectedRows = table.getSelectedRowModel().flatRows;

    const loadingRows: Record<string, boolean> = {};

    for (const row of selectedRows) {
      const nextBookingKey = getBookingKey({
        accountId: value,
        taxCodeId: row.original.taxCodeId,
        costCenterId: row.original.costCenterId,
        invoiceId: row.original.invoice.invoiceId,
        createdAt: row.original.createdAt,
        createdBy: row.original.createdBy,
        unlinked: row.original.unlinked,
      });

      let rowWithBooking = null;
      try {
        rowWithBooking = table.getRow(nextBookingKey);
      } catch (error) {
        rowWithBooking = null;
      }

      const parentRow = row.getParentRow();

      if (parentRow && parentRow.depth === 1) {
        loadingRows[parentRow.id] = true;
        loadingRows[nextBookingKey] = true;
      } else {
        loadingRows[nextBookingKey] = Boolean(rowWithBooking);
      }
    }

    table.setLoadingRow((prev) => ({
      ...prev,
      ...loadingRows,
    }));
  };

  const handleSupplierChange = async (_value: string | null) => {
    const value = _value !== SpecialFilterValue.Unknown ? _value : null;
    setLoadingRows(value);
    const toastId = toast.loading(t("generic_loading"));

    const selectedRows = table.getSelectedRowModel().flatRows;

    const parentIds = Array.from(
      new Set(
        selectedRows.map((row) => {
          return row.original.invoiceId;
        }),
      ),
    );

    try {
      table.resetRowSelection();

      await updateInvoice(parentIds, {
        supplierId: value,
      });

      // Reset the fastlane cache
      apolloClient.cache.evict({ fieldName: "fastlaneReviews" });
      apolloClient.cache.gc();

      setTimeout(() => {
        toast.dismiss(toastId);
      }, 100);

      if (value) {
        onSetSupplier(value);
      }
    } catch (_error) {
      toast.error(t("generic_error"), { richColors: true, id: toastId });
    } finally {
      table.setLoadingRow((prev) => ({}));
    }
  };

  const handleAccountChange = async (_value: string | null) => {
    const value = _value !== SpecialFilterValue.Unknown ? _value : null;
    setLoadingRows(value);
    const toastId = toast.loading(t("generic_loading"));

    try {
      table.resetRowSelection();

      await updateLineItems(lineItemIds, {
        accountId: value,
      });

      setTimeout(() => {
        toast.dismiss(toastId);
      }, 100);
    } catch (_error) {
      toast.error(t("generic_error"), { richColors: true, id: toastId });
    } finally {
      table.setLoadingRow((prev) => ({}));
    }
  };

  const handleTaxCodeChange = async (_value: string | null) => {
    const value = _value !== SpecialFilterValue.Unknown ? _value : null;
    setLoadingRows(value);
    const toastId = toast.loading(t("generic_loading"));

    try {
      table.resetRowSelection();

      await updateLineItems(lineItemIds, {
        taxCodeId: value,
      });

      setTimeout(() => {
        toast.dismiss(toastId);
      }, 100);
    } catch (_error) {
      toast.error(t("generic_error"), { richColors: true, id: toastId });
    } finally {
      table.setLoadingRow((prev) => ({}));
    }
  };

  const handleCostCenterChange = async (_value: string | null) => {
    const value = _value !== SpecialFilterValue.Unknown ? _value : null;
    setLoadingRows(value);
    const toastId = toast.loading(t("generic_loading"));

    try {
      table.resetRowSelection();

      await updateLineItems(lineItemIds, {
        costCenterId: value,
      });

      setTimeout(() => {
        toast.dismiss(toastId);
      }, 100);
    } catch (_error) {
      toast.error(t("generic_error"), { richColors: true, id: toastId });
    } finally {
      table.setLoadingRow((prev) => ({}));
    }
  };

  const handleTypeChange = async (type: InvoiceType) => {
    if (invoiceType === type) return;

    const toastId = toast.loading(t("generic_loading"));

    const selectedRows = table.getSelectedRowModel().flatRows;

    const parentIds = Array.from(
      new Set(
        selectedRows.map((row) => {
          return row.original.invoiceId;
        }),
      ),
    );

    try {
      table.resetRowSelection();

      await updateInvoiceType(parentIds, type);

      // Reset the fastlane cache since invoices moved to different type
      apolloClient.cache.evict({ fieldName: "fastlaneReviews" });
      apolloClient.cache.gc();

      setTimeout(() => {
        toast.dismiss(toastId);
      }, 100);
    } catch (_error) {
      toast.error(t("generic_error"), { richColors: true, id: toastId });
    }
  };

  const handleApproveAll = async () => {
    const onUndo = () => {
      approveLineItems(
        lineItemIds,
        {
          accountReviewed: false,
        },
        {},
      );
    };

    table.resetRowSelection();

    approveLineItems(
      lineItemIds,
      {
        accountReviewed: true,
      },
      { onUndo },
    );
  };

  const handleUnlink = async () => {
    const toastId = toast.loading(t("generic_loading"));

    try {
      const updateLineItemsUnlinkedPromises = lineItemIds.map((id) => {
        const unlinked = uuidv4();

        return updateLineItems([id], {
          unlinked,
        });
      });

      await Promise.all(updateLineItemsUnlinkedPromises);

      table.resetRowSelection();
    } catch (_error) {
      toast.error(t("generic_error"), { richColors: true, id: toastId });
    } finally {
      setTimeout(() => {
        toast.dismiss(toastId);
      }, 100);
    }
  };

  const handleDeleteInvoices = async () => {
    const toastId = toast.loading(t("generic_loading"));

    const selectedRows = table.getSelectedRowModel().flatRows;

    const parentIds = Array.from(
      new Set(
        selectedRows.map((row) => {
          return row.original.invoiceId;
        }),
      ),
    );

    try {
      table.resetRowSelection();

      await deleteInvoice(parentIds);

      // Reset the fastlane cache since invoices were deleted
      apolloClient.cache.evict({ fieldName: "fastlaneReviews" });
      apolloClient.cache.gc();

      setTimeout(() => {
        toast.dismiss(toastId);
      }, 100);
    } catch (_error) {
      toast.error(t("generic_error"), { richColors: true, id: toastId });
    }
  };

  return (
    <TableActionBarRoot>
      <DropdownMenu>
        <TableActionBarRowsSelected table={table} />
        <SupplierDropdown
          onChange={(value) => handleSupplierChange(value ?? null)}
          options={suppliers}
          placeholder={
            invoiceType === "receivable"
              ? t("fastlane.action_bar.supplier_placeholder.debtor")
              : t("fastlane.action_bar.supplier_placeholder.creditor")
          }
          fullWidth={false}
          type={invoiceType === "receivable" ? "debtor" : "creditor"}
        />
        <BuDropdown
          onChange={handleTaxCodeChange}
          items={taxCodes ?? []}
          placeholder="BU Wählen"
          variant={"outline"}
          fullWidth={false}
        />
        <AccountsDropdown
          onChange={handleAccountChange}
          items={accounts ?? []}
          variant="outline"
          allowUnknownAccount
          fullWidth={false}
          placeholder="Sachkonto Wählen"
          className=""
        />
        {hasCostCenters && (
          <CostCenterDropdown
            onChange={handleCostCenterChange}
            items={costCenters ?? []}
            placeholder="Kostenstelle Wählen"
            variant="outline"
            fullWidth={false}
          />
        )}
        <TypeSelect currentType={invoiceType} onSelect={handleTypeChange} />
        <Button onClick={handleApproveAll} className="gap-2">
          <Check size={14} />
          {t("reviewed")}
        </Button>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="px-3">
            <EllipsisVertical size={16} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-72"
          align="end"
          side="top"
          sideOffset={10}
        >
          <DropdownMenuGroup>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuItem
                  onSelect={handleUnlink}
                  className="cursor-pointer gap-2"
                >
                  <Split size={16} />
                  <span>
                    {t("separate_bookings.label", {
                      count: lineItemIds.length,
                    })}
                  </span>
                </DropdownMenuItem>
              </TooltipTrigger>
              <TooltipContent align="end">
                {t("separate_bookings.tooltip")}
              </TooltipContent>
            </Tooltip>
            <DropdownMenuItem
              onSelect={handleDeleteInvoices}
              className="cursor-pointer gap-2 text-destructive"
            >
              <Trash2 size={16} />
              <span>{t("delete_invoice")}</span>
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </TableActionBarRoot>
  );
};
